import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { AbstractControl, UntypedFormGroup } from '@angular/forms'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { forkJoin, Observable, of, Subject } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import { PersonaFieldTypes } from '../enums/persona-fields-types.enum'
import { PersonaField } from '../interfaces/persona-field.interface'
import { Persona, PersonaCommunicationPayload, Personas } from '../interfaces/persona.interface'
import { PersonaFactoryService } from '../models/persona.model'

/**
 * Handles all persona backend apis and operations
 */
@Injectable({
  providedIn: 'root'
})
export class PersonaService {
  isValidPersonas$ = new Subject<boolean>()
  /**
   * Creates the service and injects it's dependencies
   * @param http HttpClient
   * @param auth AuthService
   */
  constructor(private http: HttpClient, private auth: AuthService, private pf: PersonaFactoryService) {}
  /**
   * Creates new personas for a specified event
   * @param event_id number
   * @param forms Persona[]
   * @returns Observable
   */
  publishForms(event_id: number, forms: any[]): Observable<any> {
    if (!forms.length) {
      return of([])
    }
    return this.http.post(`${environment.planningApi}forms`, {
      event_id: event_id,
      forms: forms
    })
  }

  /**
   * Updates personas for a specified event
   * @param event_id number
   * @param forms Persona[]
   * @returns Observable
   */
  updateForms(event_id: number, forms: any[]): Observable<any> {
    if (!forms.length) {
      return of([])
    }
    return this.http.put(`${environment.planningApi}forms`, {
      event_id: event_id,
      forms: forms
    })
  }

  /**
   * Updates personas for a specified event
   * @param id number
   * @param options
   * @returns Observable Partial<Persona>
   */
  patchForm(id: number): Observable<Partial<Persona>> {
    return this.http.patch<Partial<Persona>>(`${environment.planningApi}forms`, {
      id,
      options: { auto_settle: true }
    })
  }

  /**
   * Deletes a list of forms by their ids
   * @param forms number[]
   * @returns Observable
   */
  deleteForms(forms: number[]) {
    return this.http.post(`${environment.planningApi}forms/delete`, {
      form_id: forms
    })
  }
  /**
   * Creates a list of form elements
   * @param elements PersonaField[]
   * @returns Observable
   */
  createFormElements(formId: number, elements: any[]): Observable<any> {
    return this.http.post(`${environment.planningApi}formElements`, {
      form_id: formId,
      form_elements: elements
    })
  }

  /**
   * Updates a list of form elements
   * @param elements PersonaField[]
   * @returns Observable
   */
  updateForElements(elements: any[]): Observable<any> {
    return this.http.patch(`${environment.planningApi}formElements`, {
      form_elements: elements
    })
  }

  /**
   * Creates form element values for a persona
   * @param form_slug persona slug
   * @param event_id event id
   * @param values {name:string, slug: string}[]
   * @returns Observable
   * @deprecated
   */
  createFormElementValues(form_slug, event_id: number, values: any[]): Observable<any> {
    return this.http.post(`${environment.planningApi}formElementValues`, {
      form_slug,
      event_id,
      form_element_values: values
    })
  }

  /**
   * Deletes a single form element value for a persona field
   * @param data form element value
   * @returns Observable
   * @deprecated
   */
  deleteFormElementValue(data: any): Observable<any> {
    return this.http.post(`${environment.planningApi}formElementValues/delete`, {
      ...data
    })
  }

  /**
   * Deletes a list of persona fields by their ids
   * @param data number[]
   * @returns Observable
   * @deprecated
   */
  deleteElement(data: any[]): Observable<any> {
    return this.http.post(`${environment.planningApi}formElements/delete`, {
      form_elements_id: [...data]
    })
  }

  /**
   * Creates event timings for an event by it's id
   * @param eventId number
   * @param eventTiming ServerRepresentedEventTiming[]
   * @returns Observable
   */
  createEventTiming(eventId: number, eventTiming: any[]) {
    return this.http.post(`${environment.planningApi}eventTimings`, {
      event_id: eventId,
      event_timings: eventTiming
    })
  }

  /**
   * Gets a list of event timing for an event by it's id
   * @param eventId number
   * @returns Observable
   */
  getEventTiming(eventId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.planningApi}eventTimings?event_id=${eventId}`)
  }

  /**
   * Updates event timings for an event by it's id
   * @param eventId number
   * @param eventTiming ServerRepresentedEventTiming[]
   * @returns Observable
   */
  updateEventTiming(eventId: number, eventTiming: any[]) {
    return this.http.put(`${environment.planningApi}eventTimings`, {
      event_id: eventId,
      event_timings: eventTiming
    })
  }

  /**
   * Updates a persona for an event
   * @param form Persona
   * @returns Observable
   */
  updateForm(form) {
    !form.parent && delete form.parent
    return this.http.patch(`${environment.planningApi}forms`, {
      ...form,
      event_id: form['event_id']
    })
  }

  /**
   * Patches Confirmations & Reminders for one persona.
   * @param organizationId Workspace id
   * @param eventId Event id
   * @param personaId Persona id
   * @param payload Communication fields
   */
  updatePersonaCommunication(organizationId: number, eventId: number, personaId: number, payload: PersonaCommunicationPayload): Observable<unknown> {
    return this.http.patch(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas/${personaId}/communication`, payload)
  }

  /**
   * Keeps the existing forms create/update calls, then patches communication for each saved persona.
   * @param organizationId Workspace id
   * @param eventId Event id
   * @param create Personas without ids
   * @param update Personas with ids
   */
  publishAndUpdateFormsWithCommunication(organizationId: number, eventId: number, create: Personas, update: Personas): Observable<{ created: any[]; updated: any[] }> {
    const createCommunications = create.map((persona) => ({
      slug: persona.slug,
      payload: this.pf.toCommunicationPayload(persona)
    }))
    const updateCommunications = update.map((persona) => ({
      id: persona.id,
      payload: this.pf.toCommunicationPayload(persona)
    }))

    return forkJoin([this.publishForms(eventId, create), this.updateForms(eventId, update)]).pipe(
      switchMap(([created, updated]: [any[], any[]]) => {
        const createdList = created ?? []
        const updatedList = updated ?? []
        const patches: Observable<unknown>[] = [
          ...updateCommunications.map(({ id, payload }) => this.updatePersonaCommunication(organizationId, eventId, id, payload)),
          ...createCommunications.map(({ slug, payload }) => {
            const personaId = createdList.find((persona) => persona?.slug === slug)?.id
            return personaId ? this.updatePersonaCommunication(organizationId, eventId, personaId, payload) : of(null)
          })
        ]
        if (!patches.length) {
          return of({ created: createdList, updated: updatedList })
        }
        return forkJoin(patches).pipe(map(() => ({ created: createdList, updated: updatedList })))
      })
    )
  }

  /**
   * @param handshake
   * @returns Observable
   */
  updateHandShake(handshake) {
    return this.http.patch(`${environment.planningApi}forms/handshake`, handshake)
  }

  save(event, personas, deletedPersonas) {
    personas.forEach((persona) => {
      if (persona.enable_payment && !persona.registration_amount) persona.registration_amount = 0
      if (!persona.enable_payment) delete persona.registration_amount
      persona =
        persona.enable_payment && persona.registration_amount > 0 && persona.require_approval
          ? this.addPreApprovalPriceMessage(persona)
          : this.removePreApprovalPriceMessage(persona)
      delete persona.promo_codes
    })
    const request = this.pf.toRequest(personas, event)
    if (deletedPersonas.length) {
      this.deleteForms(deletedPersonas).subscribe(() => {})
    }
    const organizationId = (this.auth.currentParentWorkspace || this.auth.currentWorkspace)?.id
    this.publishAndUpdateFormsWithCommunication(organizationId, event.id, request.create, request.update)
      .pipe(
        catchError(() => {
          return of(false)
        })
      )
      .subscribe()
  }

  addPreApprovalPriceMessage(persona: Persona) {
    let approvalPaymentMessageField: PersonaField = persona.form_elements.filter((o) => o.options?.approval_payment_message)[0]
    if (!approvalPaymentMessageField) {
      approvalPaymentMessageField = {
        form_element_type_id: PersonaFieldTypes.WARNING_MESSAGE,
        is_disable_online: false,
        is_disable_onsite: true,
        is_required: 0,
        is_unique: false,
        label: 'Please note upon approval of your application, you’ll receive a payment ink to complete the process.',
        name: 'warn_line',
        options: { approval_payment_message: true },
        weight: 100
      }
      persona.form_elements.push(approvalPaymentMessageField)
    }
    return persona
  }

  removePreApprovalPriceMessage(persona: Persona) {
    persona.form_elements = persona.form_elements.filter((personaField) => !personaField.options?.approval_payment_message)
    return persona
  }
  /**
   * Generate new slugs
   */
  generateNewPersonaSlugs(personas: AbstractControl): void {
    const letters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z']

    personas['controls'].forEach((personaFormGroup: UntypedFormGroup) => {
      const persona = personaFormGroup.value
      const oldSlugs = personas.value.map((persona: Persona) => persona.slug.toLowerCase())
      if (persona.id) return

      // Catch persona name
      const name = persona.name.toLowerCase().trim()
      // Generate Intial value for slug
      let slug = name.replace(/[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{1,}/g, '-')
      // Remove last character if it was a hyphen - or first character if it was a hyphen -
      slug = slug.replace(/^-{1,}/, '').replace(/-{1,}$/, '')

      // A pattern To check if used language is english or not
      const englishPattern = /^[A-Za-z0-9-]*$/
      // To remove any non english letters if there any
      if (!englishPattern.test(slug)) {
        slug = name
          .replace(/[^a-zA-Z0-9]+/g, '')
          .replace(/[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{1,}/g, '')
          .replace('-', '')
          .trim()
          .slice(0, 3)

        // Replace slug with vis if slug length is less than 3
        if (slug.length < 3) slug = 'vis'
      }

      // Initialize a new string for new slug
      let newSlug = ''
      // Check if the slug already exists or not to generate a new one based on the last one
      if (oldSlugs.includes(slug)) {
        const lastPersona: Persona = personas.value
          .filter((p: Persona) => p.slug.toLowerCase().includes(slug) && p.slug.toLowerCase().replace(slug, '').split('').length <= 1)
          .at(-1)
        const postfixLetters = lastPersona.slug.replace(slug, '').split('')
        if (postfixLetters.join('').replace(/-/g, '').length <= 1) {
          const newLetterIndex = letters.indexOf(postfixLetters.reverse()[0]) + 1
          newSlug = slug + (!letters[newLetterIndex] ? 'aa' : letters[newLetterIndex])
        } else if (postfixLetters.join('').replace(/-/g, '').length > 1) {
          const firstLetterIndex = letters.indexOf(postfixLetters[0])
          const lastLetterIndex = letters.indexOf(postfixLetters.reverse()[0])
          newSlug =
            slug +
            (!letters[lastLetterIndex + 1] ? letters[firstLetterIndex + 1] : letters[firstLetterIndex]) +
            (letters[lastLetterIndex + 1] ? letters[lastLetterIndex + 1] : letters[0])
        }
      }

      // Patch slug value to the form control
      personaFormGroup.patchValue({
        slug: newSlug || slug
      })
    })
  }

  createGroupForm(payload: any) {
    return this.http.post(`${environment.planningApi}admin/group_form/create`, payload)
  }

  /**
   * Saves confirmation setup options for a specific workspace/event/form selection.
   * This endpoint is used by the admin Confirmation Setup tool.
   */
  updateConfirmationSettings(payload: { workspace_id: number; event_id: number; form_id: number; editLabel: string; options: Record<string, boolean | string | number> }) {
    return this.http.post(`${environment.planningApi}admin/workspace/forms`, payload)
  }
}
